import { useMemo, useState } from 'react'
import useSWR, { mutate } from 'swr'
import useSWRMutation from 'swr/mutation'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import dayjs from 'dayjs'
import { toast } from 'sonner'
import { ChevronDown, ChevronRight, Loader2, Plus } from 'lucide-react'

import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import Skeleton from '@/components/ui/skeleton'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import {
  PRODUCT_TREE_ADMIN_URL,
  PRODUCT_TREE_URL,
  countryLabel,
  walkGroups,
} from '@/comm/product-tree'
import { postFetcher, putFetcher } from '@/hooks'
import { useRoles } from '@/hooks/auth'
import { Role } from '@/constants/role'
import { cn } from '@/lib/utils'

// 与服务端 dto/product-tree.dto.ts 保持一致
const GROUP_CODE_PATTERN = /^[a-z0-9_]{1,32}$/
const COUNTRY_CODE_PATTERN = /^([A-Z]{2}|[a-z0-9]{2,8})$/
const NAME_MAX_LENGTH = 64

const KIND_LABELS = { root: '根', line: '产品线', sub: '子组' }
const PLATFORM_OPTIONS = [
  { value: 'android', label: 'Android' },
  { value: 'ios', label: 'iOS' },
  { value: 'other', label: '其他' },
]
const REGION_OPTIONS = [
  { value: 'core', label: '核心' },
  { value: 'jpkr', label: '日韩' },
  { value: 'other', label: '其他' },
]
const REGION_LABELS = Object.fromEntries(
  REGION_OPTIONS.map((o) => [o.value, o.label]),
)

const toBool = (value) => value === true || Number(value) === 1

const nameSchema = z
  .string()
  .trim()
  .min(1, '请填写名称')
  .max(NAME_MAX_LENGTH, `不超过 ${NAME_MAX_LENGTH} 个字符`)
const sortSchema = z.coerce.number({ invalid_type_error: '请填写整数' }).int('请填写整数')

/** 保存成功后刷新 admin 树与全站产品树（comm 常量随之原地刷新） */
const refreshTrees = () => {
  mutate([PRODUCT_TREE_ADMIN_URL])
  mutate([PRODUCT_TREE_URL])
}

const formatUpdated = (row) => {
  if (!row.updatedBy && !row.updatedAt) return '-'
  const at = row.updatedAt ? dayjs(row.updatedAt).format('YYYY-MM-DD HH:mm') : ''
  return [row.updatedBy, at].filter(Boolean).join(' · ')
}

const StatusBadge = ({ active }) =>
  toBool(active) ? (
    <Badge variant="outline" size="sm">启用</Badge>
  ) : (
    <Badge size="sm">停用</Badge>
  )

/** 产品与分组、国家配置：分组码 / 国家码是查询与权限参数，只能新建不能改 */
const ProductTreePage = () => {
  const canEdit = useRoles([Role.BuyerLeader, Role.RevenuerLeader])
  const { data, isLoading, error } = useSWR(
    canEdit ? [PRODUCT_TREE_ADMIN_URL] : null,
  )

  if (!canEdit) {
    return (
      <Alert>
        <AlertTitle>无权限</AlertTitle>
        <AlertDescription>仅 Admin、投放负责人、变现负责人可配置产品与国家</AlertDescription>
      </Alert>
    )
  }
  if (isLoading) {
    return (
      <div className="flex flex-col gap-2">
        <Skeleton className="h-9 w-64" />
        <Skeleton className="h-96 w-full" />
      </div>
    )
  }
  if (error) {
    return (
      <Alert variant="destructive">
        <AlertTitle>加载失败</AlertTitle>
        <AlertDescription>{error.message}</AlertDescription>
      </Alert>
    )
  }

  return (
    <div className="flex flex-col gap-4">
      <p className="text-sm text-muted-foreground">
        分组码、pid、国家码是查询参数和权限串的一部分，不可修改；名称、归属、排序、启用状态保存后刷新页面即在各页下拉生效。停用只影响下拉展示，不影响权限展开与历史数据聚合。
      </p>
      <Tabs defaultValue="products">
        <TabsList>
          <TabsTrigger value="products">产品与分组</TabsTrigger>
          <TabsTrigger value="countries">国家</TabsTrigger>
        </TabsList>
        <TabsContent value="products">
          <ProductsTab groups={data?.groups ?? []} />
        </TabsContent>
        <TabsContent value="countries">
          <CountriesTab countries={data?.countries ?? []} />
        </TabsContent>
      </Tabs>
    </div>
  )
}

/* ---------------- 产品与分组 ---------------- */

const ProductsTab = ({ groups }) => {
  const [collapsed, setCollapsed] = useState(() => new Set())
  const [editing, setEditing] = useState(null)

  // 全部分组（含停用），供归属 / 父组下拉；descendants 用于防止把分组挂到自己的后代下
  const groupOptions = useMemo(() => {
    const list = []
    walkGroups(groups, (group, ancestors) => {
      list.push({
        code: group.code,
        name: group.name,
        kind: group.kind,
        active: toBool(group.active),
        depth: ancestors.length,
        ancestors: ancestors.map((g) => g.code),
      })
    })
    return list
  }, [groups])

  const rows = useMemo(() => {
    const list = []
    const visit = (group, depth) => {
      list.push({ type: 'group', depth, row: group })
      if (collapsed.has(group.code)) return
      for (const p of group.pids ?? []) {
        list.push({ type: 'pid', depth: depth + 1, row: p })
      }
      for (const child of group.children ?? []) visit(child, depth + 1)
    }
    groups.forEach((g) => visit(g, 0))
    return list
  }, [groups, collapsed])

  const toggle = (code) =>
    setCollapsed((prev) => {
      const next = new Set(prev)
      if (next.has(code)) next.delete(code)
      else next.add(code)
      return next
    })

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center justify-between gap-2">
        <p className="text-sm text-muted-foreground">
          分组按父子关系展开；pid 挂在最窄的分组下。
        </p>
        <Button size="sm" onClick={() => setEditing({ type: 'newGroup' })}>
          <Plus data-icon="inline-start" />
          新建分组
        </Button>
      </div>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>名称</TableHead>
            <TableHead className="w-32">编码 / pid</TableHead>
            <TableHead className="w-24">类型</TableHead>
            <TableHead className="w-24">平台</TableHead>
            <TableHead className="w-16">排序</TableHead>
            <TableHead className="w-16">状态</TableHead>
            <TableHead className="w-56">最近修改</TableHead>
            <TableHead className="w-20 text-right">操作</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map(({ type, depth, row }) =>
            type === 'group' ? (
              <TableRow key={`g-${row.code}`}>
                <TableCell>
                  <div
                    className="flex items-center gap-1"
                    style={{ paddingLeft: depth * 20 }}
                  >
                    <Button
                      variant="ghost"
                      size="icon"
                      className="size-6"
                      aria-label={collapsed.has(row.code) ? '展开' : '收起'}
                      onClick={() => toggle(row.code)}
                    >
                      {collapsed.has(row.code) ? <ChevronRight /> : <ChevronDown />}
                    </Button>
                    <span className="font-medium">{row.name}</span>
                  </div>
                </TableCell>
                <TableCell className="font-mono">{row.code}</TableCell>
                <TableCell>
                  <Badge variant="outline" size="sm">
                    {KIND_LABELS[row.kind] ?? row.kind}
                  </Badge>
                </TableCell>
                <TableCell className="text-muted-foreground">-</TableCell>
                <TableCell>{row.sort}</TableCell>
                <TableCell>
                  <StatusBadge active={row.active} />
                </TableCell>
                <TableCell className="text-sm text-muted-foreground">
                  {formatUpdated(row)}
                </TableCell>
                <TableCell className="text-right">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setEditing({ type: 'group', row })}
                  >
                    编辑
                  </Button>
                </TableCell>
              </TableRow>
            ) : (
              <TableRow
                key={`p-${row.pid}`}
                className={cn(!toBool(row.active) && 'text-muted-foreground')}
              >
                <TableCell>
                  <div style={{ paddingLeft: depth * 20 + 28 }}>{row.name}</div>
                </TableCell>
                <TableCell className="font-mono">{row.pid}</TableCell>
                <TableCell className="text-muted-foreground">产品</TableCell>
                <TableCell>
                  {PLATFORM_OPTIONS.find((o) => o.value === row.platform)?.label ??
                    row.platform}
                </TableCell>
                <TableCell>{row.sort}</TableCell>
                <TableCell>
                  <StatusBadge active={row.active} />
                </TableCell>
                <TableCell className="text-sm text-muted-foreground">
                  {formatUpdated(row)}
                </TableCell>
                <TableCell className="text-right">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setEditing({ type: 'pid', row })}
                  >
                    编辑
                  </Button>
                </TableCell>
              </TableRow>
            ),
          )}
        </TableBody>
      </Table>

      <Dialog
        open={Boolean(editing)}
        onOpenChange={(open) => !open && setEditing(null)}
      >
        <DialogContent className="sm:max-w-md">
          {editing?.type === 'pid' && (
            <PidForm
              key={editing.row.pid}
              row={editing.row}
              groupOptions={groupOptions}
              onDone={() => setEditing(null)}
            />
          )}
          {editing?.type === 'group' && (
            <GroupForm
              key={editing.row.code}
              row={editing.row}
              groupOptions={groupOptions}
              onDone={() => setEditing(null)}
            />
          )}
          {editing?.type === 'newGroup' && (
            <GroupForm groupOptions={groupOptions} onDone={() => setEditing(null)} />
          )}
        </DialogContent>
      </Dialog>
    </div>
  )
}

const GroupSelectItems = ({ options }) => (
  <SelectGroup>
    {options.map((g) => (
      <SelectItem
        key={g.code}
        value={g.code}
        style={{ paddingLeft: 8 + g.depth * 16 }}
      >
        {g.name}（{g.code}）{g.active ? '' : ' · 停用'}
      </SelectItem>
    ))}
  </SelectGroup>
)

const SubmitButton = ({ pending, children }) => (
  <Button type="submit" disabled={pending}>
    {pending && <Loader2 data-icon="inline-start" className="animate-spin" />}
    {pending ? '保存中...' : children}
  </Button>
)

/** 保存并提示；失败时保留表单 */
const useSave = (url, fetcher, onDone, successText) => {
  const { trigger, isMutating } = useSWRMutation(url, fetcher)
  const save = async (body) => {
    try {
      await trigger(body)
      refreshTrees()
      toast.success(successText)
      onDone()
    } catch (e) {
      toast.error(`保存失败：${e.message}`)
    }
  }
  return { save, isMutating }
}

const NameField = ({ form }) => (
  <FormField
    control={form.control}
    name="name"
    render={({ field }) => (
      <FormItem>
        <FormLabel>名称</FormLabel>
        <FormControl>
          <Input maxLength={NAME_MAX_LENGTH} {...field} />
        </FormControl>
        <FormMessage />
      </FormItem>
    )}
  />
)

const SortField = ({ form }) => (
  <FormField
    control={form.control}
    name="sort"
    render={({ field }) => (
      <FormItem>
        <FormLabel>排序</FormLabel>
        <FormControl>
          <Input type="number" step={1} {...field} />
        </FormControl>
        <FormDescription>同级内按数值从小到大排列</FormDescription>
        <FormMessage />
      </FormItem>
    )}
  />
)

const CheckboxField = ({ form, name, label, description }) => (
  <FormField
    control={form.control}
    name={name}
    render={({ field }) => (
      <FormItem>
        <div className="flex items-start gap-3">
          <FormControl>
            <Checkbox
              checked={field.value}
              onCheckedChange={(v) => field.onChange(v === true)}
            />
          </FormControl>
          <div className="flex flex-col gap-1">
            <FormLabel>{label}</FormLabel>
            {description && <FormDescription>{description}</FormDescription>}
          </div>
        </div>
      </FormItem>
    )}
  />
)

const SelectField = ({ form, name, label, placeholder, disabled, children }) => (
  <FormField
    control={form.control}
    name={name}
    render={({ field }) => (
      <FormItem>
        <FormLabel>{label}</FormLabel>
        <Select value={field.value} onValueChange={field.onChange} disabled={disabled}>
          <FormControl>
            <SelectTrigger>
              <SelectValue placeholder={placeholder} />
            </SelectTrigger>
          </FormControl>
          <SelectContent>{children}</SelectContent>
        </Select>
        <FormMessage />
      </FormItem>
    )}
  />
)

const pidSchema = z.object({
  name: nameSchema,
  groupCode: z.string().regex(GROUP_CODE_PATTERN, '请选择分组'),
  platform: z.enum(['android', 'ios', 'other']),
  sort: sortSchema,
  active: z.boolean(),
})

const PidForm = ({ row, groupOptions, onDone }) => {
  const form = useForm({
    resolver: zodResolver(pidSchema),
    defaultValues: {
      name: row.name,
      groupCode: row.groupCode,
      platform: row.platform,
      sort: row.sort,
      active: toBool(row.active),
    },
  })
  const { save, isMutating } = useSave(
    `/products/${row.pid}`,
    putFetcher,
    onDone,
    `已保存 pid ${row.pid}`,
  )

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(save)} className="flex flex-col gap-4">
        <DialogHeader>
          <DialogTitle>编辑产品 · pid {row.pid}</DialogTitle>
          <DialogDescription>pid 不可修改；换分组后该 pid 的分组权限按新归属展开。</DialogDescription>
        </DialogHeader>
        <NameField form={form} />
        <SelectField form={form} name="groupCode" label="所属分组" placeholder="选择分组">
          <GroupSelectItems options={groupOptions.filter((g) => g.kind !== 'root')} />
        </SelectField>
        <SelectField form={form} name="platform" label="平台">
          <SelectGroup>
            {PLATFORM_OPTIONS.map((o) => (
              <SelectItem key={o.value} value={o.value}>
                {o.label}
              </SelectItem>
            ))}
          </SelectGroup>
        </SelectField>
        <SortField form={form} />
        <CheckboxField form={form} name="active" label="启用" description="停用后不在下拉中显示" />
        <DialogFooter>
          <SubmitButton pending={isMutating}>保存</SubmitButton>
        </DialogFooter>
      </form>
    </Form>
  )
}

const groupSchema = z.object({
  code: z.string().trim().regex(GROUP_CODE_PATTERN, '小写字母、数字、下划线，1–32 位'),
  name: nameSchema,
  parentCode: z.string().optional(),
  kind: z.enum(['root', 'line', 'sub']),
  sort: sortSchema,
  active: z.boolean(),
})

const GroupForm = ({ row, groupOptions, onDone }) => {
  const isNew = !row
  const isRoot = row?.kind === 'root'
  const rootCode = groupOptions.find((g) => g.kind === 'root')?.code
  const form = useForm({
    resolver: zodResolver(
      groupSchema.refine((v) => v.kind === 'root' || Boolean(v.parentCode), {
        message: '请选择父分组',
        path: ['parentCode'],
      }),
    ),
    defaultValues: {
      code: row?.code ?? '',
      name: row?.name ?? '',
      parentCode: row?.parentCode ?? rootCode ?? '',
      kind: row?.kind ?? 'line',
      sort: row?.sort ?? 0,
      active: row ? toBool(row.active) : true,
    },
  })
  const { save, isMutating } = useSave(
    isNew ? '/product-groups' : `/product-groups/${row.code}`,
    isNew ? postFetcher : putFetcher,
    onDone,
    isNew ? '已新建分组' : `已保存分组 ${row.code}`,
  )

  // 父组候选：排除自己和自己的后代
  const parentOptions = groupOptions.filter(
    (g) => !row || (g.code !== row.code && !g.ancestors.includes(row.code)),
  )

  const onSubmit = ({ code, name, parentCode, kind, sort, active }) => {
    if (isNew) return save({ code, name, parentCode, kind, sort, active })
    if (isRoot) return save({ name, sort, active })
    return save({ name, parentCode, kind, sort, active })
  }

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="flex flex-col gap-4">
        <DialogHeader>
          <DialogTitle>{isNew ? '新建分组' : `编辑分组 · ${row.code}`}</DialogTitle>
          <DialogDescription>
            分组码会出现在权限串（如 total1_1-BR）和查询参数里，建好后不可修改。
          </DialogDescription>
        </DialogHeader>
        {isNew && (
          <FormField
            control={form.control}
            name="code"
            render={({ field }) => (
              <FormItem>
                <FormLabel>分组码</FormLabel>
                <FormControl>
                  <Input placeholder="如 total6" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        )}
        <NameField form={form} />
        {!isRoot && (
          <>
            <SelectField form={form} name="parentCode" label="父分组" placeholder="选择父分组">
              <GroupSelectItems options={parentOptions} />
            </SelectField>
            <SelectField form={form} name="kind" label="类型">
              <SelectGroup>
                <SelectItem value="line">{KIND_LABELS.line}</SelectItem>
                <SelectItem value="sub">{KIND_LABELS.sub}</SelectItem>
              </SelectGroup>
            </SelectField>
          </>
        )}
        <SortField form={form} />
        <CheckboxField
          form={form}
          name="active"
          label="启用"
          description="停用后该分组及其下产品不在下拉中显示"
        />
        <DialogFooter>
          <SubmitButton pending={isMutating}>{isNew ? '新建' : '保存'}</SubmitButton>
        </DialogFooter>
      </form>
    </Form>
  )
}

/* ---------------- 国家 ---------------- */

const CountriesTab = ({ countries }) => {
  const [editing, setEditing] = useState(null)
  const nextSort = useMemo(
    () => Math.max(0, ...countries.map((c) => Number(c.sort) || 0)) + 10,
    [countries],
  )

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center justify-between gap-2">
        <p className="text-sm text-muted-foreground">
          核心国在下拉里显示为「名称 *」；Baseline 列表取勾选了 baseline 的国家，按排序排列。
        </p>
        <Button size="sm" onClick={() => setEditing({ type: 'new' })}>
          <Plus data-icon="inline-start" />
          新建国家
        </Button>
      </div>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-20">国家码</TableHead>
            <TableHead>名称</TableHead>
            <TableHead className="w-28">下拉显示</TableHead>
            <TableHead className="w-20">区域</TableHead>
            <TableHead className="w-16">核心</TableHead>
            <TableHead className="w-20">Baseline</TableHead>
            <TableHead className="w-16">排序</TableHead>
            <TableHead className="w-16">状态</TableHead>
            <TableHead className="w-56">最近修改</TableHead>
            <TableHead className="w-20 text-right">操作</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {countries.map((c) => (
            <TableRow
              key={c.code}
              className={cn(!toBool(c.active) && 'text-muted-foreground')}
            >
              <TableCell className="font-mono">{c.code}</TableCell>
              <TableCell>{c.name}</TableCell>
              <TableCell>{countryLabel({ ...c, core: toBool(c.core) })}</TableCell>
              <TableCell>{REGION_LABELS[c.region] ?? c.region}</TableCell>
              <TableCell>{toBool(c.core) ? '是' : '-'}</TableCell>
              <TableCell>{toBool(c.baseline) ? '是' : '-'}</TableCell>
              <TableCell>{c.sort}</TableCell>
              <TableCell>
                <StatusBadge active={c.active} />
              </TableCell>
              <TableCell className="text-sm text-muted-foreground">
                {formatUpdated(c)}
              </TableCell>
              <TableCell className="text-right">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setEditing({ type: 'edit', row: c })}
                >
                  编辑
                </Button>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      <Dialog
        open={Boolean(editing)}
        onOpenChange={(open) => !open && setEditing(null)}
      >
        <DialogContent className="sm:max-w-md">
          {editing && (
            <CountryForm
              key={editing.row?.code ?? 'new'}
              row={editing.row}
              nextSort={nextSort}
              onDone={() => setEditing(null)}
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  )
}

const countrySchema = z.object({
  code: z.string().trim().regex(COUNTRY_CODE_PATTERN, 'ISO2 大写（如 BR），或 2–8 位小写字母数字'),
  name: nameSchema,
  region: z.enum(['core', 'jpkr', 'other']),
  core: z.boolean(),
  baseline: z.boolean(),
  sort: sortSchema,
  active: z.boolean(),
})

const CountryForm = ({ row, nextSort, onDone }) => {
  const isNew = !row
  const form = useForm({
    resolver: zodResolver(countrySchema),
    defaultValues: {
      code: row?.code ?? '',
      name: row?.name ?? '',
      region: row?.region ?? 'other',
      core: row ? toBool(row.core) : false,
      baseline: row ? toBool(row.baseline) : false,
      sort: row?.sort ?? nextSort,
      active: row ? toBool(row.active) : true,
    },
  })
  const { save, isMutating } = useSave(
    isNew ? '/countries' : `/countries/${row.code}`,
    isNew ? postFetcher : putFetcher,
    onDone,
    isNew ? '已新建国家' : `已保存 ${row.code}`,
  )

  const onSubmit = ({ code, ...rest }) => save(isNew ? { code, ...rest } : rest)

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="flex flex-col gap-4">
        <DialogHeader>
          <DialogTitle>{isNew ? '新建国家' : `编辑国家 · ${row.code}`}</DialogTitle>
          <DialogDescription>国家码是查询参数和权限串的一部分，建好后不可修改。</DialogDescription>
        </DialogHeader>
        {isNew && (
          <FormField
            control={form.control}
            name="code"
            render={({ field }) => (
              <FormItem>
                <FormLabel>国家码</FormLabel>
                <FormControl>
                  <Input placeholder="如 BR" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        )}
        <NameField form={form} />
        <SelectField form={form} name="region" label="区域">
          <SelectGroup>
            {REGION_OPTIONS.map((o) => (
              <SelectItem key={o.value} value={o.value}>
                {o.label}
              </SelectItem>
            ))}
          </SelectGroup>
        </SelectField>
        <CheckboxField form={form} name="core" label="核心国" description="下拉中名称后显示「 *」" />
        <CheckboxField form={form} name="baseline" label="Baseline" description="出现在 dr / cost / profit baseline 的国家列表" />
        <SortField form={form} />
        <CheckboxField form={form} name="active" label="启用" description="停用后不在下拉中显示" />
        <DialogFooter>
          <SubmitButton pending={isMutating}>{isNew ? '新建' : '保存'}</SubmitButton>
        </DialogFooter>
      </form>
    </Form>
  )
}

export default ProductTreePage
